import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2 } from 'lucide-react'
import { AxiosError } from 'axios'

import { Button } from '@/common/ui/button'
import { RtpConfirmDialog } from '@/modules/promotion/components/RtpConfirmDialog'
import { Form } from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Card, CardContent } from '@/common/ui/card'
import UploadImage from '@/common/components/UploadImage'
import Spinner from '@/common/components/Spinner'
import GameSelector from '@/common/components/GameSelector'
import { EXCLUDED_GAME_NAMES } from '@/common/constants/excludedGames'
import useMutationCreateLuckyWheelImageFileUrl from '@/modules/operator/api/hooks/useMutationCreateLuckyWheelImageFileUrl'
import useMutationCreateArrowLuckyWheelImageFileUrl from '@/modules/operator/api/hooks/useMutationCreateArrowLuckyWheelImageFileUrl'
import useQueryGetPresetId from '@/modules/promotion/api/hooks/useQueryGetPresetId'
import useQueryGetGameList from '@/modules/game/api/hooks/useQueryGetGameList'
import WheelSetupModal from './components/WheelSetupModal'
import { ProbabilityWarningDialog } from './components/ProbabilityWarningDialog'
import { ProbabilityLowWarningDialog } from './components/ProbabilityLowWarningDialog'
import ConditionSelector from './components/ConditionSelector'
import LuckyWheelImagePresetModal from '../LuckyWheelImagePresetModal'
import { IsActiveLuckyWheel } from '../IsActiveLuckyWheel'
import { ShowImageOnWheelLuckyWheel } from '../ShowImageOnWheelLuckyWheel'
import { RequireConnectedLineLuckyWheel } from '../RequireConnectedLineLuckyWheel'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'

import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

import useMutationUpdateLuckyWheel from '@/modules/promotion/api/hooks/useMutateUpdateLuckyWheel'

import {
	UpdateLuckyWheelFormSchema,
	UpdateLuckyWheelFormValues,
} from './components/FormSchema'
import { GetLuckyWheelResponse } from '@/modules/promotion/type'
import {
	WalletConditionSection,
	prepareWalletConditionForSubmit,
} from '@/common/components/WalletConditionSection'
import { ProviderResolutionConflictDialog } from '../../../ProviderResolutionConflictDialog'
// import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
// import { Calendar } from '@/common/ui/calendar'
// import { CalendarIcon } from 'lucide-react'
// import { cn } from '@/common/lib/utils'
// import { format } from 'date-fns'
// import {
// 	Select,
// 	SelectContent,
// 	SelectItem,
// 	SelectTrigger,
// 	SelectValue,
// } from '@/common/ui/select'

const preventMinusInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
	if (e.key === '-' || e.key === 'e') {
		e.preventDefault()
	}
}

interface UpdateLuckyWheelFormProps {
	defaultValues: GetLuckyWheelResponse
	isLoading: boolean
}

export default function UpdateLuckyWheelForm({
	defaultValues,
	isLoading,
}: UpdateLuckyWheelFormProps) {
	const navigate = useNavigate()
	const [showSetupModal, setShowSetupModal] = useState(false)
	const [showProbabilityWarning, setShowProbabilityWarning] = useState(false)
	const [showLowProbabilityWarning, setShowLowProbabilityWarning] =
		useState(false)
	const [conflictDialogOpen, setConflictDialogOpen] = useState(false)
	const [conflictMessage, setConflictMessage] = useState('')
	const [showRtpConfirm, setShowRtpConfirm] = useState(false)

	const { mutateAsync: getLuckyWheelImageFileUrl } =
		useMutationCreateLuckyWheelImageFileUrl()
	const { mutateAsync: getArrowLuckyWheelImageFileUrl } =
		useMutationCreateArrowLuckyWheelImageFileUrl()

	// Fetch presets and games for FREE_SPIN
	const { data: presets } = useQueryGetPresetId()
	const { data: gamesData } = useQueryGetGameList({
		providerName: 'PGHARD',
		skip: 0,
		take: 1000,
	})

	// Filter out excluded games by name
	const filteredGames = useMemo(() => {
		if (!gamesData?.data) return []
		return gamesData.data.filter(
			(game) => !EXCLUDED_GAME_NAMES.includes(game.name),
		)
	}, [gamesData])

	// Show setup modal when component mounts
	useEffect(() => {
		setShowSetupModal(true)
	}, [])

	const form = useForm<UpdateLuckyWheelFormValues>({
		resolver: zodResolver(UpdateLuckyWheelFormSchema),
		defaultValues: {
			...(defaultValues && {
				isActive: defaultValues.isActive,
				requireConnectedLine: defaultValues.requireConnectedLine ?? false,
				expiredIn: defaultValues.expiredIn
					? new Date(defaultValues.expiredIn)
					: new Date(),
				turnoverRound: defaultValues.turnoverRound ?? 0,
				claimConstraint: defaultValues.claimConstraint ?? [],
				wheelSetting:
					defaultValues.wheelSetting?.map((setting) => ({
						...setting,
						id: setting.id ?? crypto.randomUUID(),
						probability: (setting.probability ?? 0) * 100,
						type: setting.type || 'CREDIT',
						color: setting.color || '#000000',
						presetId: setting.presetId || undefined,
						gameId: setting.gameId || undefined,
						betAmount: setting.betAmount || undefined,
					})) ?? [],
				wheelUrl: defaultValues.wheelUrl || '',
				arrowWheelUrl: defaultValues.arrowWheelUrl || '',
				showImageOnWheel: defaultValues.showImageOnWheel ?? false,
				walletCondition: {
					minimumWithdrawCredit:
						defaultValues.walletCondition?.minimumWithdrawCredit ?? undefined,
					maximumWithdrawCredit:
						defaultValues.walletCondition?.maximumWithdrawCredit ?? undefined,
					turnoverRound:
						defaultValues.walletCondition?.turnoverRound ?? undefined,
					gameTypes: defaultValues.walletCondition?.gameTypes ?? [],
					whitelistGameProviders:
						defaultValues.walletCondition?.whitelistGameProviders ?? [],
					usePool: defaultValues.walletCondition?.usePool ?? false,
					poolPresetConfigs:
						defaultValues.walletCondition?.poolPresetConfigs ?? [],
					presetConfigs: defaultValues.walletCondition?.presetConfigs ?? [],
					pgHard: (defaultValues.walletCondition as any)?.pgHard ?? false,
					jokerHard: (defaultValues.walletCondition as any)?.jokerHard ?? false,
					jiliHard: (defaultValues.walletCondition as any)?.jiliHard ?? false,
					ppHard: (defaultValues.walletCondition as any)?.ppHard ?? false,
					nlcHard: (defaultValues.walletCondition as any)?.nlcHard ?? false,
				},
			}),
		},
		mode: 'onChange',
	})

	// Watch wheel settings to lock amount to 1 for FREE_SPIN types
	const wheelSettings = form.watch('wheelSetting')
	useEffect(() => {
		wheelSettings.forEach((setting, index) => {
			if (setting.type === 'FREE_SPIN' && setting.amount !== 1) {
				form.setValue(`wheelSetting.${index}.amount`, 1)
			}
		})
	}, [wheelSettings, form])

	const { mutate: updateLuckyWheel } = useMutationUpdateLuckyWheel({
		onSuccess: () => {
			toast.success('อัพเดทสำเร็จ')
			navigate('/operator/lucky-wheel')
		},
		onError: (error) => {
			const axiosError = error as unknown as AxiosError<{
				message: string
				errorCode: string
			}>

			// Check if it's a provider resolution conflict error
			if (
				axiosError.isAxiosError &&
				axiosError.response?.data?.errorCode === 'PROVIDER_RESOLUTION_CONFLICT'
			) {
				setConflictMessage(axiosError.response.data.message)
				setConflictDialogOpen(true)
				return
			}

			// For other errors, show toast
			showErrorToast(error)
		},
	})

	if (isLoading || !defaultValues) return <Spinner />

	const onSubmit = async (values: UpdateLuckyWheelFormValues) => {
		try {
			// Check total probability before submitting
			const totalProbability = values.wheelSetting.reduce(
				(sum, setting) => sum + (setting.probability || 0),
				0,
			)

			if (totalProbability > 100) {
				setShowProbabilityWarning(true)
				return // Don't submit the form
			}

			if (totalProbability < 100) {
				setShowLowProbabilityWarning(true)
				return // Don't submit the form
			}

			const wheelSettingWithRatio = values.wheelSetting.map((setting) => ({
				...setting,
				type: setting.type || 'CREDIT',
				probability: setting.probability / 100,
				// Include FREE_SPIN specific fields if type is FREE_SPIN
				...(setting.type === 'FREE_SPIN' && {
					presetId: setting.presetId,
					gameId: setting.gameId,
					betAmount: setting.betAmount,
				}),
			}))

			const payload = {
				...values,
				wheelSetting: wheelSettingWithRatio,
				expiredIn: values.expiredIn.toISOString(),
				claimConstraint: values.claimConstraint ?? [],
				walletCondition: prepareWalletConditionForSubmit(
					values.walletCondition,
				),
				// TODO: remove this after backend is updated
				turnoverRound: 1,
			}

			updateLuckyWheel({ payload: payload as any })
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const addWheelSetting = () => {
		const currentSettings = form.getValues('wheelSetting')
		form.setValue('wheelSetting', [
			...currentSettings,
			{
				id: crypto.randomUUID(),
				title: '',
				probability: 0,
				type: 'CREDIT',
				amount: 0,
				color: '#000000',
				presetId: undefined,
				gameId: undefined,
				betAmount: undefined,
			},
		])
	}

	const removeWheelSetting = (index: number) => {
		const currentSettings = form.getValues('wheelSetting')
		form.setValue(
			'wheelSetting',
			currentSettings.filter((_, i) => i !== index),
		)
	}

	const currentWheelSettings = form.watch('wheelSetting')
	const currentTotalProbability = currentWheelSettings.reduce(
		(sum, setting) => sum + (setting.probability || 0),
		0,
	)

	return (
		<>
			<WheelSetupModal
				isOpen={showSetupModal}
				onClose={() => setShowSetupModal(false)}
			/>
			<ProbabilityWarningDialog
				isOpen={showProbabilityWarning}
				onClose={() => setShowProbabilityWarning(false)}
				totalProbability={currentTotalProbability}
			/>
			<ProbabilityLowWarningDialog
				isOpen={showLowProbabilityWarning}
				onClose={() => setShowLowProbabilityWarning(false)}
				totalProbability={currentTotalProbability}
			/>
			<ProviderResolutionConflictDialog
				isOpen={conflictDialogOpen}
				onClose={() => setConflictDialogOpen(false)}
				message={conflictMessage}
			/>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit, showFormValidationToast)}
					className="space-y-6"
					noValidate
				>
					<Card>
						<CardContent className="pt-6">
							<h3 className="mb-4 text-lg font-medium">
								เงื่อนไขการรับโควตาหมุน
							</h3>
							<p className="mb-4 text-sm text-muted-foreground">
								เงื่อนไขการรับโควตาหมุนจะทำงานแบบ หรือ/อย่างใดอย่างหนึ่ง (OR)
								หมายความว่า ผู้เล่นไม่จำเป็นต้องทำครบทุกเงื่อนไข หากมีเงื่อนไข 3
								ข้อ ผู้เล่นสามารถทำเงื่อนไขใดเงื่อนไขหนึ่งเพื่อรับโควตา 1 โควตา
								ดังนั้น หากทำครบหลายเงื่อนไข
								ผู้เล่นจะได้รับโควตาตามจำนวนเงื่อนไขที่ทำได้
							</p>
							<div>
								<ConditionSelector
									control={form.control}
									name="claimConstraint"
								/>
							</div>

							<div className="flex flex-wrap gap-2">
								<IsActiveLuckyWheel
									wheelData={defaultValues}
									onUpdate={(isActive) => form.setValue('isActive', isActive)}
								/>
								<ShowImageOnWheelLuckyWheel
									wheelData={defaultValues}
									onUpdate={(showImageOnWheel) =>
										form.setValue('showImageOnWheel', showImageOnWheel)
									}
								/>
								<RequireConnectedLineLuckyWheel
									wheelData={defaultValues}
									onUpdate={(requireConnectedLine) =>
										form.setValue('requireConnectedLine', requireConnectedLine)
									}
								/>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardContent className="pt-6">
							<h3 className="mb-4 text-lg font-medium">รูปภาพกงล้อ</h3>
							{form.watch('showImageOnWheel') ? (
								<div className="grid grid-cols-2 gap-6">
									<FormField
										control={form.control}
										name="wheelUrl"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													รูปภาพกงล้อ{' '}
													<span className="text-amber-600 dark:text-amber-400">
														(สัดส่วน 1:1, เช่น 240x240)
													</span>
												</FormLabel>
												<FormControl>
													<UploadImage
														onImageUploaded={(fileUrl) =>
															field.onChange(fileUrl)
														}
														defaultValue={field.value}
														getFileUrl={({ payload }) =>
															getLuckyWheelImageFileUrl({
																payload: {
																	fileExtension: payload.fileExtension,
																},
															})
														}
														aspectRatio="1/1"
														previewHeight={240}
													/>
												</FormControl>
												<div className="mt-2">
													<LuckyWheelImagePresetModal
														type="wheel"
														onSelect={(url) => field.onChange(url)}
													/>
												</div>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="arrowWheelUrl"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													รูปภาพลูกศร{' '}
													<span className="text-amber-600 dark:text-amber-400">
														(สัดส่วน 35:55)
													</span>
												</FormLabel>
												<FormControl>
													<UploadImage
														onImageUploaded={(fileUrl) =>
															field.onChange(fileUrl)
														}
														defaultValue={field.value}
														getFileUrl={({ payload }) =>
															getArrowLuckyWheelImageFileUrl({
																payload: {
																	fileExtension: payload.fileExtension,
																},
															})
														}
														aspectRatio="35/55"
														previewHeight={128}
													/>
												</FormControl>
												<div className="mt-2">
													<LuckyWheelImagePresetModal
														type="arrow"
														onSelect={(url) => field.onChange(url)}
													/>
												</div>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							) : (
								<div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
									<p className="text-muted-foreground">
										เปิดใช้งาน &ldquo;แสดงรูปภาพบนกงล้อ&rdquo;
										เพื่ออัพโหลดรูปภาพ
									</p>
									<p className="mt-2 text-sm text-muted-foreground">
										ขณะนี้จะใช้วงล้อเริ่มต้นและตั้งค่าสีในการแสดงผลแทน
									</p>
								</div>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardContent className="pt-6">
							<div className="mb-4 flex items-center justify-between">
								<h3 className="text-lg font-medium">ตั้งค่ากงล้อ</h3>
								<Button type="button" onClick={addWheelSetting}>
									<Plus className="mr-2 h-4 w-4" />
									เพิ่มกงล้อ
								</Button>
							</div>
							<div className="space-y-4">
								{form.watch('wheelSetting').map((setting, index) => {
									const isFreeSpin =
										form.watch(`wheelSetting.${index}.type`) === 'FREE_SPIN'

									return (
										<div key={setting.id} className="space-y-4">
											<div
												className="grid items-end gap-4"
												style={{
													gridTemplateColumns: form.watch('showImageOnWheel')
														? '1.5fr 1fr 1fr 1fr auto'
														: '1.5fr 1fr 1fr 1fr 1fr auto',
												}}
											>
												<FormField
													control={form.control}
													name={`wheelSetting.${index}.title`}
													render={({ field }) => (
														<FormItem>
															<FormLabel>ชื่อรางวัล</FormLabel>
															<FormControl>
																<Input {...field} />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name={`wheelSetting.${index}.probability`}
													render={({ field }) => (
														<FormItem>
															<FormLabel>เปอร์เซ็นต์การออกรางวัล (%)</FormLabel>
															<FormControl>
																<Input
																	type="number"
																	min={0}
																	max={100}
																	onKeyDown={preventMinusInput}
																	{...field}
																	onChange={(e) =>
																		field.onChange(
																			e.target.value
																				? Number(e.target.value)
																				: null,
																		)
																	}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>

												<FormField
													control={form.control}
													name={`wheelSetting.${index}.type`}
													render={({ field }) => (
														<FormItem>
															<FormLabel>ประเภทรางวัล</FormLabel>
															<Select
																onValueChange={field.onChange}
																value={field.value}
															>
																<FormControl>
																	<SelectTrigger>
																		<SelectValue placeholder="เลือกประเภทรางวัล" />
																	</SelectTrigger>
																</FormControl>
																<SelectContent>
																	<SelectItem value="CREDIT">เครดิต</SelectItem>
																	<SelectItem value="DIAMOND">เพชร</SelectItem>
																	<SelectItem value="FREE_SPIN">
																		ฟรีสปิน
																	</SelectItem>
																</SelectContent>
															</Select>
															<FormMessage />
														</FormItem>
													)}
												/>

												<FormField
													control={form.control}
													name={`wheelSetting.${index}.amount`}
													render={({ field }) => (
														<FormItem>
															<FormLabel>
																<span className="text-sm text-muted-foreground">
																	จำนวนจ่าย
																	{isFreeSpin && (
																		<p className="text-sm text-muted-foreground">
																			ฟรีสปินจำกัดจำนวนรางวัลที่ 1 อันเท่านั้น
																		</p>
																	)}
																</span>
															</FormLabel>
															<FormControl>
																<Input
																	type="number"
																	onKeyDown={preventMinusInput}
																	{...field}
																	value={isFreeSpin ? 1 : field.value}
																	onChange={(e) =>
																		field.onChange(
																			e.target.value
																				? Number(e.target.value)
																				: null,
																		)
																	}
																	disabled={isFreeSpin}
																/>
															</FormControl>

															<FormMessage />
														</FormItem>
													)}
												/>

												{!form.watch('showImageOnWheel') && (
													<FormField
														control={form.control}
														name={`wheelSetting.${index}.color`}
														render={({ field }) => (
															<FormItem>
																<FormLabel>สีรางวัล</FormLabel>
																<FormControl>
																	<Input type="color" {...field} />
																</FormControl>
																<FormMessage />
															</FormItem>
														)}
													/>
												)}

												<div className="flex gap-2">
													<Button
														type="button"
														variant="destructive"
														size="icon"
														className="self-end"
														onClick={() => removeWheelSetting(index)}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											</div>

											{/* FREE_SPIN fields in separate grid */}
											{isFreeSpin && (
												<div className="grid grid-cols-3 gap-4 border-l-4 border-blue-500 pl-4">
													<FormField
														control={form.control}
														name={`wheelSetting.${index}.presetId`}
														render={({ field }) => (
															<FormItem>
																<FormLabel>
																	RTP Preset (สำหรับฟรีสปิน)
																</FormLabel>
																<Select
																	onValueChange={field.onChange}
																	value={field.value}
																>
																	<FormControl>
																		<SelectTrigger>
																			<SelectValue placeholder="เลือก RTP Preset" />
																		</SelectTrigger>
																	</FormControl>
																	<SelectContent>
																		{presets?.presets.map((preset) => (
																			<SelectItem
																				key={preset.id}
																				value={preset.id}
																			>
																				{preset.note}
																			</SelectItem>
																		))}
																	</SelectContent>
																</Select>
																<FormMessage />
															</FormItem>
														)}
													/>

													<FormField
														control={form.control}
														name={`wheelSetting.${index}.gameId`}
														render={({ field }) => (
															<GameSelector
																field={field}
																games={filteredGames}
																required
															/>
														)}
													/>

													<FormField
														control={form.control}
														name={`wheelSetting.${index}.betAmount`}
														render={({ field }) => (
															<FormItem>
																<FormLabel required>ราคาฟรีสปิน</FormLabel>
																<FormControl>
																	<Input
																		type="number"
																		placeholder="0"
																		{...field}
																		onNumberChange={(value) =>
																			field.onChange(value)
																		}
																	/>
																</FormControl>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											)}
										</div>
									)
								})}
							</div>
						</CardContent>
					</Card>

					{/* Wallet Condition Section */}
					<WalletConditionSection<UpdateLuckyWheelFormValues>
						control={form.control}
						fieldPrefix="walletCondition"
						showGameSettings={true}
						showPoolPreset={true}
					/>

					<div className="flex gap-4">
						<Button
							type="button"
							variant="default"
							onClick={() => {
								form.handleSubmit(
									() => setShowRtpConfirm(true),
									showFormValidationToast,
								)()
							}}
						>
							บันทึก
						</Button>
					</div>
				</form>
			</Form>
			<RtpConfirmDialog
				open={showRtpConfirm}
				onOpenChange={setShowRtpConfirm}
				onConfirm={() => {
					setShowRtpConfirm(false)
					form.handleSubmit(
						(values) => onSubmit(values),
						showFormValidationToast,
					)()
				}}
				confirmLabel="บันทึก"
			/>
		</>
	)
}
